import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useLocalization } from '../../../../../config/localization';
import Icon, { ICONS } from '../../../../ui/icons';
import SearchablePromptSelect, { type PromptOption } from '../../../../ui/SearchablePromptSelect';
import EnhancerEndpointsSummary, { type EnhancerEndpointUrl } from './EnhancerEndpointsSummary';
import WorkflowUrlEditModal, { type WorkflowUrlDraft } from './WorkflowUrlEditModal';
import type { BaseSectionProps, WorkflowStepConfig, StepOutputMapping } from './index';

/** Maximum number of pipeline steps allowed per workflow. */
const MAX_STEPS = 10;

/** url_index of the Prompt Enhance endpoint (see src/utils/workflowUrlRoles.ts). */
const PROMPT_ENHANCE_URL_INDEX = 2;

/** Creates a default empty step configuration for a new pipeline step. */
function createDefaultStep(): WorkflowStepConfig {
  return {
    name: '',
    url: 'https://api.loremax.ai/v1/workflows/run',
    api_key: '',
    output_mappings: [],
  };
}

/**
 * Admin section for configuring multi-step workflow pipeline steps.
 *
 * Renders a list of collapsible step cards, each with fields for name, URL,
 * API key, timeout, prompt body, output mappings, and extra JSON parameters.
 * Steps can be added (up to {@link MAX_STEPS}), removed, and reordered.
 *
 * Data is stored in `formData.settings.steps` and persisted via the
 * existing unified workflow save flow.
 */
export default function PipelineStepsSection({
  formData,
  onFormDataChange,
  workflow,
  language
}: BaseSectionProps) {
  const { getToken } = useAuth();
  const getTokenRef = useRef(getToken);
  getTokenRef.current = getToken;
  const t = useLocalization(language);
  const steps = formData.settings?.steps || [];
  const [expandedSteps, setExpandedSteps] = useState<Record<number, boolean>>({});
  const [workflowPrompts, setWorkflowPrompts] = useState<PromptOption[]>([]);
  const [workflowPromptsLoadError, setWorkflowPromptsLoadError] = useState(false);
  const [workflowUrls, setWorkflowUrls] = useState<EnhancerEndpointUrl[]>([]);
  const [workflowUrlsLoadError, setWorkflowUrlsLoadError] = useState(false);
  const [promptsLoading, setPromptsLoading] = useState(false);
  const [showApiKeys, setShowApiKeys] = useState<Record<number, boolean>>({});
  const [enhanceDraft, setEnhanceDraft] = useState<WorkflowUrlDraft | null>(null);
  const [enhanceSaving, setEnhanceSaving] = useState(false);
  const [enhanceSaveError, setEnhanceSaveError] = useState<string | null>(null);
  const [urlsReloadToken, setUrlsReloadToken] = useState(0);

  const cfg = t.admin?.pipelineStepsConfig;

  useEffect(() => {
    const loadWorkflowPrompts = async () => {
      try {
        setPromptsLoading(true);
        setWorkflowPrompts([]);
        setWorkflowPromptsLoadError(false);
        const token = await getTokenRef.current({ template: 'withmail' });
        const response = await fetch('/api/admin/prompts/workflow', {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (!response.ok) {
          throw new Error(`Failed to load workflow prompts (HTTP ${response.status})`);
        }
        const data = await response.json();
        if (!Array.isArray(data?.prompts)) {
          throw new Error('Workflow prompt catalog response is malformed');
        }
        setWorkflowPrompts(data.prompts);
        setWorkflowPromptsLoadError(false);
      } catch (error) {
        console.error('Error loading workflow prompts:', error);
        setWorkflowPrompts([]);
        setWorkflowPromptsLoadError(true);
      } finally {
        setPromptsLoading(false);
      }
    };
    loadWorkflowPrompts();
  }, []);

  useEffect(() => {
    const workflowId = workflow?.id;
    if (!workflowId) {
      setWorkflowUrls([]);
      setWorkflowUrlsLoadError(false);
      return;
    }
    let active = true;
    setWorkflowUrls([]);
    setWorkflowUrlsLoadError(false);

    const loadWorkflowUrls = async () => {
      try {
        const token = await getTokenRef.current({ template: 'withmail' });
        const response = await fetch(`/api/admin/workflows/${workflowId}/urls`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error(`Failed to load workflow URLs (HTTP ${response.status})`);
        }
        const data = await response.json();
        if (!Array.isArray(data?.urls)) {
          throw new Error('Workflow URLs response is malformed');
        }
        if (active) {
          setWorkflowUrls(data.urls);
          setWorkflowUrlsLoadError(false);
        }
      } catch (error) {
        if (active) {
          console.error('Error loading workflow URLs for pipeline summary:', error);
          setWorkflowUrls([]);
          setWorkflowUrlsLoadError(true);
        }
      }
    };

    void loadWorkflowUrls();
    return () => {
      active = false;
    };
  }, [workflow?.id, urlsReloadToken]);

  /** Seed the edit modal from the current Prompt Enhance row (or a create draft). */
  const openEnhanceEditor = () => {
    const existing = workflowUrls.find((url) => url.url_index === PROMPT_ENHANCE_URL_INDEX);
    let parsedExtraParams: Record<string, string> = {};
    if (existing?.extra_params) {
      try {
        parsedExtraParams = JSON.parse(existing.extra_params);
      } catch (error) {
        console.error('Failed to parse extra_params:', error);
      }
    }

    setEnhanceSaveError(null);
    setEnhanceDraft(existing
      ? {
          id: existing.id,
          url_index: existing.url_index,
          url: existing.url,
          api_key: existing.api_key || '',
          description: existing.description || '',
          prompt_id: existing.prompt_id ?? null,
          dify_workflow_url: existing.dify_workflow_url || '',
          extra_params: parsedExtraParams,
        }
      : {
          url_index: PROMPT_ENHANCE_URL_INDEX,
          url: 'https://api.loremax.ai/v1/workflows/run',
          api_key: '',
          description: 'Prompt Enhance',
          prompt_id: null,
          dify_workflow_url: '',
          extra_params: {},
        });
  };

  /** PUT (existing) / POST (new) the Prompt Enhance URL, then re-fetch the summary. */
  const saveEnhanceDraft = async () => {
    if (!enhanceDraft || !workflow?.id) return;

    try {
      setEnhanceSaving(true);
      setEnhanceSaveError(null);
      const token = await getTokenRef.current({ template: 'withmail' });
      const payload = {
        url_index: enhanceDraft.url_index,
        url: enhanceDraft.url,
        api_key: enhanceDraft.api_key || null,
        description: enhanceDraft.description,
        prompt_id: enhanceDraft.prompt_id ? Number(enhanceDraft.prompt_id) : null,
        dify_workflow_url: enhanceDraft.dify_workflow_url || null,
        extra_params: enhanceDraft.extra_params && Object.keys(enhanceDraft.extra_params).length > 0
          ? JSON.stringify(enhanceDraft.extra_params)
          : null
      };

      const response = await fetch(
        enhanceDraft.id
          ? `/api/admin/workflows/${workflow.id}/urls/${enhanceDraft.url_index}`
          : `/api/admin/workflows/${workflow.id}/urls`,
        {
          method: enhanceDraft.id ? 'PUT' : 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(payload)
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to save URL (HTTP ${response.status})`);
      }

      setEnhanceDraft(null);
      setUrlsReloadToken((token_) => token_ + 1);
    } catch (error) {
      console.error('Error saving Prompt Enhance URL:', error);
      setEnhanceSaveError(t.adminWorkflows?.urlManager?.saveError || 'Failed to save URL');
    } finally {
      setEnhanceSaving(false);
    }
  };

  const updateSteps = (newSteps: WorkflowStepConfig[]) => {
    onFormDataChange('settings', {
      ...(formData.settings || {}),
      steps: newSteps.length > 0 ? newSteps : undefined
    });
  };

  const updateStep = (index: number, updates: Partial<WorkflowStepConfig>) => {
    const updated = [...steps];
    updated[index] = { ...updated[index], ...updates };
    updateSteps(updated);
  };

  const addMapping = (stepIndex: number) => {
    const step = steps[stepIndex];
    const newMappings: StepOutputMapping[] = [...step.output_mappings, { source: '', target: '' }];
    updateStep(stepIndex, { output_mappings: newMappings });
  };

  const updateMapping = (stepIndex: number, mappingIndex: number, field: 'source' | 'target', value: string) => {
    const step = steps[stepIndex];
    const newMappings = [...step.output_mappings];
    newMappings[mappingIndex] = { ...newMappings[mappingIndex], [field]: value };
    updateStep(stepIndex, { output_mappings: newMappings });
  };

  const removeMapping = (stepIndex: number, mappingIndex: number) => {
    const step = steps[stepIndex];
    const newMappings = step.output_mappings.filter((_, i) => i !== mappingIndex);
    updateStep(stepIndex, { output_mappings: newMappings });
  };

  const addExtraParam = (stepIndex: number) => {
    const step = steps[stepIndex];
    const params = { ...(step.extra_params || {}) };
    let idx = Object.keys(params).length + 1;
    while (`param${idx}` in params) idx++;
    params[`param${idx}`] = '';
    updateStep(stepIndex, { extra_params: params });
  };

  const updateExtraParamKey = (stepIndex: number, oldKey: string, newKey: string) => {
    if (newKey === oldKey) return;
    const step = steps[stepIndex];
    const params = step.extra_params || {};
    if (newKey in params) return;
    // Rebuild object preserving entry order (replace key in-place)
    // so that index-based React keys stay stable during rename
    const newParams: Record<string, any> = {};
    for (const [k, v] of Object.entries(params)) {
      newParams[k === oldKey ? newKey : k] = v;
    }
    updateStep(stepIndex, { extra_params: newParams });
  };

  const updateExtraParamValue = (stepIndex: number, key: string, value: string) => {
    const step = steps[stepIndex];
    const params = { ...(step.extra_params || {}) };
    // Preserve numeric types: if the value is a valid finite number string, store as number
    // (e.g. "1024" → 1024). Otherwise keep as string. Uses isFinite to reject Infinity/NaN
    // and avoids JSON.parse surprises with "null", "true", "false", objects, or arrays.
    const trimmed = value.trim();
    params[key] = trimmed !== '' && isFinite(Number(trimmed)) ? Number(trimmed) : value;
    updateStep(stepIndex, { extra_params: params });
  };

  const removeExtraParam = (stepIndex: number, key: string) => {
    const step = steps[stepIndex];
    const params = { ...(step.extra_params || {}) };
    delete params[key];
    updateStep(stepIndex, { extra_params: Object.keys(params).length > 0 ? params : undefined });
  };

  const addStep = () => {
    if (steps.length >= MAX_STEPS) return;
    const newSteps = [...steps, createDefaultStep()];
    updateSteps(newSteps);
    setExpandedSteps(prev => ({ ...prev, [newSteps.length - 1]: true }));
  };

  const removeStep = (index: number) => {
    const newSteps = steps.filter((_, i) => i !== index);
    updateSteps(newSteps);
    // Clean up position-indexed state maps
    const reindex = (prev: Record<number, any>) => {
      const next: Record<number, any> = {};
      for (const [key, val] of Object.entries(prev)) {
        const k = Number(key);
        if (k < index) next[k] = val;
        else if (k > index) next[k - 1] = val;
      }
      return next;
    };
    setExpandedSteps(reindex);
    setShowApiKeys(reindex);
  };

  const moveStepUp = (index: number) => {
    if (index <= 0) return;
    const newSteps = [...steps];
    [newSteps[index - 1], newSteps[index]] = [newSteps[index], newSteps[index - 1]];
    updateSteps(newSteps);
    const swapKeys = (prev: Record<number, any>) => {
      const next = { ...prev };
      const a = prev[index - 1];
      const b = prev[index];
      if (a !== undefined || b !== undefined) {
        next[index - 1] = b;
        next[index] = a;
      }
      return next;
    };
    setExpandedSteps(swapKeys);
    setShowApiKeys(swapKeys);
  };

  const moveStepDown = (index: number) => {
    if (index >= steps.length - 1) return;
    const newSteps = [...steps];
    [newSteps[index], newSteps[index + 1]] = [newSteps[index + 1], newSteps[index]];
    updateSteps(newSteps);
    const swapKeys = (prev: Record<number, any>) => {
      const next = { ...prev };
      const a = prev[index];
      const b = prev[index + 1];
      if (a !== undefined || b !== undefined) {
        next[index] = b;
        next[index + 1] = a;
      }
      return next;
    };
    setExpandedSteps(swapKeys);
    setShowApiKeys(swapKeys);
  };

  const toggleExpanded = (index: number) => {
    setExpandedSteps(prev => ({ ...prev, [index]: !prev[index] }));
  };

  const isExpanded = (index: number) => expandedSteps[index] ?? false;

  return (
    <div className="space-y-6">
      <section>
        <h3 className="text-lg font-semibold text-white mb-2 flex items-center gap-2">
          <Icon name={ICONS.LAYERS} size={20} className="text-blue-400" />
          {cfg?.title || 'Pipeline Steps'}
        </h3>
        <p className="text-sm text-gray-400 mb-6">
          {cfg?.description || 'Configure multi-step pipeline execution for this workflow'}
        </p>

        {workflow && (
          <div className="mb-6">
            <EnhancerEndpointsSummary
              urls={workflowUrls}
              prompts={workflowPrompts}
              language={language}
              loadError={workflowUrlsLoadError || workflowPromptsLoadError}
              onEditPromptEnhance={openEnhanceEditor}
            />
            {enhanceSaveError && (
              <p className="mt-2 text-sm text-red-400" role="alert">
                {enhanceSaveError}
              </p>
            )}
          </div>
        )}

        {enhanceDraft && (
          <WorkflowUrlEditModal
            value={enhanceDraft}
            onChange={setEnhanceDraft}
            onSave={() => { void saveEnhanceDraft(); }}
            onCancel={() => setEnhanceDraft(null)}
            prompts={workflowPrompts}
            promptsLoading={promptsLoading}
            saving={enhanceSaving}
            language={language}
          />
        )}

        {steps.length === 0 ? (
          <div className="text-center py-12 border border-dashed border-gray-700 rounded-lg" data-testid="pipeline-empty-state">
            <Icon name={ICONS.LAYERS} size={48} className="mx-auto mb-4 text-gray-500" />
            <p className="text-gray-400 mb-4">
              {cfg?.emptyState || 'No pipeline steps configured. Add a step to create a multi-step workflow.'}
            </p>
            <button
              type="button"
              onClick={addStep}
              className="inline-flex items-center gap-1.5 px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
              data-testid="pipeline-add-first-step"
            >
              <Icon name={ICONS.PLUS} size={14} />
              {cfg?.addStep || 'Add Step'}
            </button>
          </div>
        ) : (
          <>
            <div className="space-y-3">
          {steps.map((step, index) => (
            <div
              key={index}
              className="border border-gray-700 rounded-lg overflow-hidden"
              data-testid={`pipeline-step-${index}`}
            >
              {/* Step Card Header */}
              <div
                className="flex items-center justify-between px-4 py-3 bg-gray-800 cursor-pointer select-none"
                onClick={() => toggleExpanded(index)}
                data-testid={`pipeline-step-header-${index}`}
              >
                <div className="flex items-center gap-2">
                  <span className="text-xs font-mono text-gray-500 text-center flex-shrink-0">
                    {`#${index + 1}`}
                  </span>
                  <span className="text-sm font-medium text-white truncate max-w-[200px]">
                    {step.name || (cfg?.stepName || 'Step Name') + '...'}
                  </span>
                </div>

                <div className="flex items-center gap-1">
                  {index > 0 && (
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); moveStepUp(index); }}
                      className="p-1.5 text-gray-400 hover:text-blue-400 hover:bg-gray-700 rounded transition-colors"
                      title={cfg?.moveUp || 'Move up'}
                      data-testid={`pipeline-step-moveup-${index}`}
                    >
                      <Icon name={ICONS.CHEVRON_UP} size={14} />
                    </button>
                  )}
                  {index < steps.length - 1 && (
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); moveStepDown(index); }}
                      className="p-1.5 text-gray-400 hover:text-blue-400 hover:bg-gray-700 rounded transition-colors"
                      title={cfg?.moveDown || 'Move down'}
                      data-testid={`pipeline-step-movedown-${index}`}
                    >
                      <Icon name={ICONS.CHEVRON_DOWN} size={14} />
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); removeStep(index); }}
                    className="p-1.5 text-gray-400 hover:text-red-400 hover:bg-gray-700 rounded transition-colors"
                    title={cfg?.deleteStep || 'Delete step'}
                    data-testid={`pipeline-step-delete-${index}`}
                  >
                    <Icon name={ICONS.TRASH} size={14} />
                  </button>
                  <div className="p-1.5 text-gray-400" title={isExpanded(index) ? (cfg?.collapseStep || 'Collapse step') : (cfg?.expandStep || 'Expand step')}>
                    <Icon name={isExpanded(index) ? ICONS.CHEVRON_UP : ICONS.CHEVRON_DOWN} size={14} />
                  </div>
                </div>
              </div>

              {/* Step Card Body (collapsible) */}
              {isExpanded(index) && (
                <div className="px-4 py-4 space-y-4 border-t border-gray-700" data-testid={`pipeline-step-body-${index}`}>
                  {/* Name */}
                  <div>
                    <label className="block text-sm text-gray-300 mb-1">
                      {cfg?.stepName || 'Step Name'}
                    </label>
                    <input
                      type="text"
                      value={step.name}
                      onChange={(e) => updateStep(index, { name: e.target.value })}
                      placeholder={cfg?.stepName || 'Step Name'}
                      className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white text-sm placeholder-gray-500 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                      data-testid={`pipeline-step-name-${index}`}
                    />
                  </div>

                  {/* API URL */}
                  <div>
                    <label className="block text-sm text-gray-300 mb-1">
                      {cfg?.stepUrl || 'API URL'}
                    </label>
                    <input
                      type="text"
                      value={step.url}
                      onChange={(e) => updateStep(index, { url: e.target.value })}
                      placeholder="https://api.example.com/v1/endpoint"
                      className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white text-sm placeholder-gray-500 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                      data-testid={`pipeline-step-url-${index}`}
                    />
                  </div>

                  {/* Workflow URL */}
                  <div>
                    <label className="block text-sm text-gray-300 mb-1">
                      {cfg?.stepWorkflowUrl || 'Workflow URL'}
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        value={step.workflow_url || ''}
                        onChange={(e) => updateStep(index, { workflow_url: e.target.value })}
                        placeholder={cfg?.stepWorkflowUrlPlaceholder || 'https://api.loremax.ai/app/.../workflow'}
                        className="w-full px-3 py-2 pr-10 bg-gray-800 border border-gray-600 rounded-lg text-white text-sm placeholder-gray-500 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                        data-testid={`pipeline-step-workflow-url-${index}`}
                      />
                      {step.workflow_url && (
                        <a
                          href={step.workflow_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-blue-400 transition-colors"
                          title={cfg?.stepWorkflowUrl || 'Open Workflow URL'}
                        >
                          <Icon name={ICONS.EXTERNAL_LINK} size={16} />
                        </a>
                      )}
                    </div>
                  </div>

                  {/* API Key */}
                  <div>
                    <label className="block text-sm text-gray-300 mb-1">
                      {cfg?.stepApiKey || 'API Key'}
                    </label>
                    <div className="relative">
                      <input
                        type={showApiKeys[index] ? 'text' : 'password'}
                        value={step.api_key}
                        onChange={(e) => updateStep(index, { api_key: e.target.value })}
                        placeholder="sk-..."
                        className="w-full px-3 py-2 pr-10 bg-gray-800 border border-gray-600 rounded-lg text-white text-sm placeholder-gray-500 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                        data-testid={`pipeline-step-apikey-${index}`}
                      />
                      <button
                        type="button"
                        onClick={() => setShowApiKeys(prev => ({ ...prev, [index]: !prev[index] }))}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
                      >
                        <Icon name={showApiKeys[index] ? ICONS.EYE_OFF : ICONS.EYE} size={16} />
                      </button>
                    </div>
                  </div>

                  {/* Workflow Prompt */}
                  <div data-testid={`pipeline-step-prompt-${index}`}>
                    <label className="block text-sm text-gray-300 mb-1">
                      {cfg?.workflowPrompt || 'Workflow Prompt'}
                    </label>
                    <SearchablePromptSelect
                      value={step.prompt_id != null ? String(step.prompt_id) : null}
                      onChange={(promptId) => {
                        const parsed = promptId != null ? parseInt(promptId, 10) : null;
                        updateStep(index, { prompt_id: parsed !== null && isNaN(parsed) ? null : parsed });
                      }}
                      prompts={workflowPrompts}
                      placeholder={cfg?.workflowPromptHint || 'Optional: Select a workflow prompt for this step'}
                      disabled={promptsLoading}
                    />
                  </div>

                  {/* Output Mappings */}
                  <div>
                    <label className="block text-sm text-gray-300 mb-2">
                      {cfg?.outputMappings || 'Output Mappings'}
                    </label>
                    <div className="space-y-2" data-testid={`pipeline-step-mappings-${index}`}>
                      {step.output_mappings.map((mapping, mIdx) => (
                        <div key={mIdx} className="flex items-center gap-2">
                          <input
                            type="text"
                            value={mapping.source}
                            onChange={(e) => updateMapping(index, mIdx, 'source', e.target.value)}
                            placeholder={cfg?.sourceField || 'Source field'}
                            className="flex-1 px-3 py-1.5 bg-gray-800 border border-gray-600 rounded-lg text-white text-sm placeholder-gray-500 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                            data-testid={`pipeline-step-${index}-mapping-source-${mIdx}`}
                          />
                          <span className="text-gray-500 text-sm flex-shrink-0">&rarr;</span>
                          <input
                            type="text"
                            value={mapping.target}
                            onChange={(e) => updateMapping(index, mIdx, 'target', e.target.value)}
                            placeholder={cfg?.targetField || 'Target field'}
                            className="flex-1 px-3 py-1.5 bg-gray-800 border border-gray-600 rounded-lg text-white text-sm placeholder-gray-500 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                            data-testid={`pipeline-step-${index}-mapping-target-${mIdx}`}
                          />
                          <button
                            type="button"
                            onClick={() => removeMapping(index, mIdx)}
                            className="p-1.5 text-gray-400 hover:text-red-400 hover:bg-gray-700 rounded transition-colors flex-shrink-0"
                            title={cfg?.deleteMapping || 'Delete mapping'}
                            data-testid={`pipeline-step-${index}-mapping-delete-${mIdx}`}
                          >
                            <Icon name={ICONS.TRASH} size={14} />
                          </button>
                        </div>
                      ))}
                      <button
                        type="button"
                        onClick={() => addMapping(index)}
                        className="flex items-center gap-1 px-2 py-1 text-xs text-blue-400 hover:text-blue-300 hover:bg-gray-800 rounded transition-colors"
                        data-testid={`pipeline-step-${index}-add-mapping`}
                      >
                        <Icon name={ICONS.PLUS} size={12} />
                        {cfg?.addMapping || 'Add mapping'}
                      </button>
                    </div>
                  </div>

                  {/* Extra Parameters (Key-Value Editor) */}
                  <div data-testid={`pipeline-step-extraparams-${index}`}>
                    <label className="block text-sm text-gray-300 mb-2">
                      {cfg?.extraParameters || 'Extra Parameters'}
                    </label>
                    <div className="space-y-2">
                      {Object.entries(step.extra_params || {}).map(([key, value], pIdx) => (
                        <div key={pIdx} className="flex items-center gap-2" data-testid={`pipeline-step-${index}-param-${pIdx}`}>
                          <input
                            type="text"
                            value={key}
                            onChange={(e) => updateExtraParamKey(index, key, e.target.value)}
                            placeholder={cfg?.parameterKey || 'Key'}
                            className="flex-1 px-3 py-1.5 bg-gray-800 border border-gray-600 rounded-lg text-white text-sm placeholder-gray-500 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                            data-testid={`pipeline-step-${index}-param-key-${pIdx}`}
                          />
                          <input
                            type="text"
                            value={String(value ?? '')}
                            onChange={(e) => updateExtraParamValue(index, key, e.target.value)}
                            placeholder={cfg?.parameterValue || 'Value'}
                            className="flex-1 px-3 py-1.5 bg-gray-800 border border-gray-600 rounded-lg text-white text-sm placeholder-gray-500 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                            data-testid={`pipeline-step-${index}-param-value-${pIdx}`}
                          />
                          <button
                            type="button"
                            onClick={() => removeExtraParam(index, key)}
                            className="p-1.5 text-gray-400 hover:text-red-400 hover:bg-gray-700 rounded transition-colors flex-shrink-0"
                            title={cfg?.deleteParameter || 'Delete parameter'}
                            data-testid={`pipeline-step-${index}-param-delete-${pIdx}`}
                          >
                            <Icon name={ICONS.TRASH} size={14} />
                          </button>
                        </div>
                      ))}
                      <button
                        type="button"
                        onClick={() => addExtraParam(index)}
                        className="flex items-center gap-1 px-2 py-1 text-xs text-blue-400 hover:text-blue-300 hover:bg-gray-800 rounded transition-colors"
                        data-testid={`pipeline-step-${index}-add-param`}
                      >
                        <Icon name={ICONS.PLUS} size={12} />
                        {cfg?.addParameter || 'Add Parameter'}
                      </button>
                    </div>
                    <p className="mt-1 text-xs text-gray-600">
                      {cfg?.extraParametersHint || 'These parameters will be added to the request data'}
                    </p>
                  </div>

                  {/* Error Handling */}
                  <div data-testid={`pipeline-step-onerror-${index}`}>
                    <label className="block text-sm text-gray-300 mb-2">
                      {cfg?.onErrorLabel || 'On Error'}
                    </label>
                    <select
                      value={step.on_error?.action || 'fail'}
                      onChange={(e) => {
                        const action = e.target.value as 'fail' | 'retry';
                        if (action === 'fail') {
                          updateStep(index, { on_error: undefined });
                        } else {
                          updateStep(index, {
                            on_error: {
                              action: 'retry',
                              max_attempts: step.on_error?.max_attempts || 2,
                              retry_delay_seconds: step.on_error?.retry_delay_seconds || 10,
                            }
                          });
                        }
                      }}
                      className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                      data-testid={`pipeline-step-onerror-select-${index}`}
                    >
                      <option value="fail">{cfg?.onErrorFail || 'Fail Pipeline'}</option>
                      <option value="retry">{cfg?.onErrorRetry || 'Retry Step'}</option>
                    </select>

                    {step.on_error?.action === 'retry' && (
                      <div className="mt-3 space-y-3 pl-3 border-l-2 border-blue-600" data-testid={`pipeline-step-retry-config-${index}`}>
                        <div>
                          <label className="block text-xs text-gray-400 mb-1">
                            {cfg?.maxAttemptsLabel || 'Max Attempts'}
                          </label>
                          <input
                            type="number"
                            min={1}
                            max={5}
                            value={step.on_error.max_attempts}
                            onChange={(e) => {
                              const val = Math.max(1, Math.min(5, parseInt(e.target.value, 10) || 1));
                              updateStep(index, {
                                on_error: { ...step.on_error!, max_attempts: val }
                              });
                            }}
                            className="w-full px-3 py-1.5 bg-gray-800 border border-gray-600 rounded-lg text-white text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                            data-testid={`pipeline-step-max-attempts-${index}`}
                          />
                          <p className="mt-0.5 text-xs text-gray-600">
                            {cfg?.maxAttemptsHint || '1 to 5 attempts'}
                          </p>
                        </div>
                        <div>
                          <label className="block text-xs text-gray-400 mb-1">
                            {cfg?.retryDelayLabel || 'Retry Delay (sec)'}
                          </label>
                          <input
                            type="number"
                            min={1}
                            max={60}
                            value={step.on_error.retry_delay_seconds}
                            onChange={(e) => {
                              const val = Math.max(1, Math.min(60, parseInt(e.target.value, 10) || 1));
                              updateStep(index, {
                                on_error: { ...step.on_error!, retry_delay_seconds: val }
                              });
                            }}
                            className="w-full px-3 py-1.5 bg-gray-800 border border-gray-600 rounded-lg text-white text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                            data-testid={`pipeline-step-retry-delay-${index}`}
                          />
                          <p className="mt-0.5 text-xs text-gray-600">
                            {cfg?.retryDelayHint || '1 to 60 seconds'}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
            </div>

            {/* Add Step button */}
            <div className="mt-4">
              {steps.length >= MAX_STEPS ? (
                <p className="text-sm text-gray-500" data-testid="pipeline-max-steps-message">
                  {cfg?.maxStepsReached || `Maximum of ${MAX_STEPS} steps reached`}
                </p>
              ) : (
                <button
                  type="button"
                  onClick={addStep}
                  className="flex items-center gap-1.5 px-3 py-2 text-sm text-blue-400 hover:text-blue-300 hover:bg-gray-800 rounded-lg transition-colors"
                  data-testid="pipeline-add-step"
                >
                  <Icon name={ICONS.PLUS} size={14} />
                  {cfg?.addStep || 'Add Step'}
                </button>
              )}
            </div>
          </>
        )}
      </section>
    </div>
  );
}
